import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Newspaper, Loader2, ShieldCheck, FileDown, CheckCircle2 } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { Emblem } from "./Emblem";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full border border-navy-border bg-navy-deep/70 px-4 py-3 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-gold";
const EMPTY = { full_name: "", organization: "", email: "", inquiry_type: "", message: "" };

export const PressSection = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [filedRef, setFiledRef] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inquiryType = form.inquiry_type || t.press.types[0];

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/press`, { ...form, inquiry_type: inquiryType });
      setFiledRef(data.reference);
      toast.success(t.press.successTitle.replace("{ref}", data.reference), {
        description: t.press.successBody,
        duration: 9000,
      });
    } catch (err) {
      toast.error(t.press.errorTitle, {
        description: err.response?.data?.detail?.[0]?.msg || t.press.errorBody,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setFiledRef(null);
    setForm(EMPTY);
  };

  return (
    <section id="press" data-testid="press-section" className="relative overflow-hidden bg-navy-deep py-24 lg:py-32">
      <img src={IMAGES.boardroom} alt="" aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.08]" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep via-navy-deep/85 to-navy-deep" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        <div className="lg:col-span-5">
          <Reveal><Overline>{t.press.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-slate-50 sm:text-4xl lg:text-5xl">
              {t.press.titleA} <span className="italic text-gold-gradient">{t.press.titleB}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-lg leading-relaxed text-slate-400">{t.press.body}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-8 flex items-center gap-3 border-l-2 border-gold/50 pl-5">
              <ShieldCheck className="h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{t.press.secureNote}</p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 flex items-center gap-5 text-gold/70">
              <Emblem className="h-20 w-[4.5rem]" />
              <div className="gold-hairline w-24" />
              <Newspaper className="h-6 w-6" strokeWidth={1.25} />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          {filedRef ? (
            <div data-testid="press-success-panel"
              className="flex h-full flex-col items-center justify-center border border-gold/40 bg-navy-card/80 p-10 text-center backdrop-blur-sm">
              <CheckCircle2 className="h-12 w-12 text-gold" strokeWidth={1.25} />
              <h3 className="mt-6 font-serif text-2xl font-medium text-slate-50">
                {t.press.successTitle.replace("{ref}", "")}
              </h3>
              <p className="mt-2 font-mono text-2xl tracking-[0.2em] text-gold" data-testid="press-reference">{filedRef}</p>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">{t.press.successBody}</p>
              <a href={`${API}/press-kit`} download data-testid="press-kit-download-button"
                className="mt-8 inline-flex items-center gap-3 bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-navy-deep transition-colors duration-300 hover:bg-gold-hover">
                <FileDown className="h-4 w-4" /> {t.press.downloadKit}
              </a>
              <button onClick={reset} data-testid="press-again-button"
                className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 underline-offset-4 transition-colors hover:text-gold hover:underline">
                {t.press.again}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="press-form"
              className="grid gap-5 border border-navy-border bg-navy-card/80 p-7 backdrop-blur-sm sm:grid-cols-2 lg:p-9">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.press.fullName}</label>
                <input required minLength={2} value={form.full_name} onChange={set("full_name")}
                  data-testid="press-full-name" placeholder={t.press.phName} className={inputCls} />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.press.org}</label>
                <input required minLength={2} value={form.organization} onChange={set("organization")}
                  data-testid="press-organization" placeholder={t.press.phOrg} className={inputCls} />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.press.email}</label>
                <input required type="email" value={form.email} onChange={set("email")}
                  data-testid="press-email" placeholder="name@media.com" className={inputCls} />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.press.type}</label>
                <select value={inquiryType} onChange={set("inquiry_type")}
                  data-testid="press-inquiry-type" className={`${inputCls} appearance-none`}>
                  {t.press.types.map((ty) => <option key={ty} value={ty} className="bg-navy-deep">{ty}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.press.message}</label>
                <textarea required minLength={10} rows={4} value={form.message} onChange={set("message")}
                  data-testid="press-message" placeholder={t.press.phMessage} className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={submitting} data-testid="press-submit-button"
                  className="flex w-full items-center justify-center gap-3 bg-gold py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-navy-deep transition-colors duration-300 hover:bg-gold-hover disabled:opacity-60">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {submitting ? t.press.submitting : t.press.submit}
                </button>
                <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.15em] leading-relaxed text-slate-500">
                  {t.press.footnote}
                </p>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
